import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Dna, Shield } from 'lucide-react';
import DNAProfilesPanel from '../components/DNAProfilesPanel';
import { BrandWordmark } from '../components/brand/BrandWordmark';
import { Badge } from '../components/ui/badge';
import { TESTER_SIMPLIFIED_UI } from '../config/testerRelease';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DNAManagement = () => {
  const navigate = useNavigate();
  const [allPeople, setAllPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Fetch all people from all trees for linking
  useEffect(() => {
    const fetchPeople = async () => {
      try {
        // Get all user's trees
        const treesResponse = await axios.get(`${API}/trees`);
        const trees = treesResponse.data || [];
        
        // Fetch people from each tree
        const allPeopleData = [];
        for (const tree of trees) {
          try {
            const peopleResponse = await axios.get(`${API}/individuals/${tree.id}`);
            const people = peopleResponse.data || [];
            people.forEach(p => {
              allPeopleData.push({
                ...p,
                tree_id: tree.id,
                tree_name: tree.name
              });
            });
          } catch (err) {
            console.error(`Error fetching people for tree ${tree.id}:`, err);
          }
        }
        
        setAllPeople(allPeopleData);
      } catch (error) {
        console.error('Error fetching people:', error);
        toast.error('Unable to load family members');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPeople();
  }, []);
  
  return (
    <div className="min-h-screen bg-background" data-testid="dna-management-page">
      {/* Navigation */}
      <nav className="border-b border-border/40 bg-background/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Link
                to="/home"
                className="hidden text-xl transition-colors hover:opacity-85 sm:block"
                data-testid="home-link"
              >
                <BrandWordmark variant="treeNav" className="text-xl" />
              </Link>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate('/dashboard')}
                className="rounded-full sm:hidden"
                data-testid="back-btn"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <span className="text-muted-foreground/40 hidden sm:block">/</span>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-xl font-serif font-semibold text-foreground" data-testid="page-title">
                    DNA Management
                  </h1>
                  {TESTER_SIMPLIFIED_UI ? (
                    <Badge variant="secondary" className="text-[10px] font-medium uppercase tracking-wide">
                      Beta · Testing
                    </Badge>
                  ) : null}
                </div>
                <p className="text-xs text-muted-foreground">
                  {TESTER_SIMPLIFIED_UI
                    ? 'Early access for testers — upload and link DNA files while we refine matching.'
                    : 'Preserve and manage DNA profiles'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/dashboard" className="text-sm text-foreground/70 hover:text-foreground hidden sm:block">
                Dashboard
              </Link>
            </div>
          </div>
        </div>
      </nav>
      
      {/* Main content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {TESTER_SIMPLIFIED_UI ? (
          <div
            className="mb-6 rounded-xl border border-amber-200/80 bg-amber-50/80 px-4 py-3 text-sm text-amber-950/90"
            data-testid="dna-beta-banner"
          >
            <strong className="font-medium">Testing feature.</strong> DNA upload is available for internal validation.
            Matching and privacy controls are still evolving — not yet the finished public experience.
          </div>
        ) : null}
        <DNAProfilesPanel allPeople={allPeople} />
      </main>
      
      {/* Footer */}
      <footer className="border-t border-border/40 mt-auto">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Shield className="w-4 h-4" />
              <span>Your DNA data is encrypted and private</span>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <Link to="/dashboard" className="hover:text-foreground">Dashboard</Link>
              <span>•</span>
              <Link to="/settings" className="hover:text-foreground">Settings</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DNAManagement;
